import { call, put, select, takeEvery, all } from 'redux-saga/effects'
import { isEmpty, get, some, filter, partition } from 'lodash'
import { toast } from 'react-toastify'

import Remote from '../../remote'

import {
  getRemoteOrderTotal,
  getRemoteOrderTotalSucceeded,
  getRemoteOrderTotalFailed,
  updateExemptions,
  updateExemptionsSucceeded,
  updateExemptionsFailed,
} from '../../actions/orderTotalRemote'
import { orderDetailsRequested } from '../../actions/order'
import { addPromotionsToOrder } from '../../actions/promotion'

import { makeGetCartItems } from '../../selectors/cart'
import { makeGetOrder } from '../../selectors/order'
import { getCustomerRefId } from '../../selectors/customer'
import { getSelectedPromotions } from '../../selectors/promotion'

import { makeLineItemData, parseModifiers } from '../../utils/orderUtils'
import { centsToDollars } from '../../utils/formatters'

import MutationApi from '../../remote/mutationApi'

const getCartItems = makeGetCartItems()
const getOrder = makeGetOrder()

function* updateExemptionsSaga(params) {
  const orderUuid = params.payload.orderUuid

  try {
    const result = yield call(MutationApi.updateTotals, params?.payload)

    yield put(updateExemptionsSucceeded({ ...result, orderUuid }))
    yield put(orderDetailsRequested(orderUuid))
  } catch (error) {
    yield put(updateExemptionsFailed({ error, orderUuid }))
  }
}

export function* watchUpdateExemptionsSaga() {
  yield takeEvery(updateExemptions.type, updateExemptionsSaga)
}

// TODO {JEFF}: A lot of redundancy between these two functions, we should try and combine them at some point so that
//              This becomes easier to maintain for the future.
function* getOrderTotal(params) {
  let orderUuid = get(params, 'payload.orderUuid', '')

  let promotions
  if (orderUuid) {
    promotions = params?.payload?.selectedPromotions
  } else {
    promotions = yield select(getSelectedPromotions)
  }
  let manualPromotions = filter(promotions, (promotion) => promotion.redemptionMethod !== 'auto')
  if (isEmpty(manualPromotions)) {
    manualPromotions = undefined
  }

  const order = yield select(getOrder, orderUuid)
  // combine order's existing line items with any that are in the cart
  let items = yield select(getCartItems)
  const itemModels = [ ...(order?.itemModels ?? []), ...(makeLineItemData(items).itemModels ?? [])]
  const standMenuUuid = get(order, 'standMenuUuid', get(order, 'createOrderParams.standMenuUuid', ''))

  const orderMenuItems = itemModels.map((lineItem) => {
    const lineItemFractionalQuantity = lineItem.fractionalQuantity ?? 1

    return {
      menuItemUuid: get(lineItem, 'menuItemUuid', get(lineItem, 'id', '')),
      quantity: lineItem.quantity,
      modifiers: parseModifiers(lineItem.modifiers, 1, lineItemFractionalQuantity),
      price: centsToDollars(lineItem.priceInCents).slice(1),
      priceInCents: lineItem.priceInCents,
      fractionalQuantity: lineItemFractionalQuantity,
    }
  })

  try {
    let result = yield call(Remote.getOrderTotal, orderMenuItems, standMenuUuid, undefined)
    if (manualPromotions) {
      result = yield call(Remote.getOrderTotal, orderMenuItems, standMenuUuid, [...manualPromotions, ...(result?.data?.promotions ?? [])])
    }

    if (result?.data?.promotions || !orderUuid) {
      const [applied, unApplied] = partition(result?.data?.promotions, (promotion) => promotion.applied || promotion.applies)
      if (!isEmpty(unApplied) && some(unApplied, (promotion) => promotion.redemptionMethod === 'manual')) {
        toast.error('Not all promotions were applied.')
      }
      if (!orderUuid) {
        yield put(addPromotionsToOrder({ selectedPromotions: applied }))
      }
    }
    yield put(getRemoteOrderTotalSucceeded(result?.data))
  } catch (err) {
    yield put(getRemoteOrderTotalFailed(err))
  }
}

function* getSplitOrderTotal(params) {
  const orderUuid = params?.payload?.orderUuid ?? ''
  const items = params?.payload?.items ?? []
  const fractionalQuantity = params?.payload?.fractionalQuantity
  const order = yield select(getOrder, orderUuid)
  const customerId = order.customerId
  const customerExternalRefId = yield select((state) => getCustomerRefId(state, customerId))
  const standMenuUuid = get(order, 'standMenuUuid', get(order, 'createOrderParams.standMenuUuid', ''))
  const orderMenuItems = items.map((lineItem) => {
    const lineItemFractionalQuantity = get(lineItem, 'fractionalQuantity', 1)

    return {
      fractionalQuantity: fractionalQuantity * lineItemFractionalQuantity,
      menuItemUuid: get(lineItem, 'menuItemUuid', get(lineItem, 'id', '')),
      quantity: lineItem.quantity,
      modifiers: parseModifiers(lineItem.modifiers, fractionalQuantity, lineItemFractionalQuantity),
      price: centsToDollars(lineItem.priceInCents).slice(1),
      priceInCents: lineItem.priceInCents,
    }
  })

  try {
    const result = yield call(Remote.getOrderTotal, orderMenuItems, standMenuUuid, customerExternalRefId, null, customerId)
    const resultNoDiscount = yield call(Remote.getOrderTotal, orderMenuItems, standMenuUuid, undefined, [], customerId)

    yield put(getRemoteOrderTotalSucceeded({ result, resultNoDiscount }))
  } catch (err) {
    yield put(getRemoteOrderTotalFailed(err))
  }
}

export function* getOrderTotalSaga(params = {}) {
  const splitOrder = params?.payload?.splitOrder

  if (splitOrder) {
    yield getSplitOrderTotal(params)
  } else {
    yield getOrderTotal(params)
  }
}

export function* watchGetOrderTotalSaga() {
  yield takeEvery(getRemoteOrderTotal.type, getOrderTotalSaga)
}
